import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ClientProjectForm.module.css";
import Button from "../../../components/UI/Button/Button";
import MultiSelectDropdown from "../../../components/UI/MultiSelectDropdown/MultiSelectDropdown";
import FeedbackModal from "../../../components/UI/FeedbackModal/FeedbackModal";
import { createProject, uploadProjectImage } from "../../../services/client-project-service";

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

const technologyOptions = [
  "HTML",
  "CSS",
  "JavaScript",
  "React",
  "ReactNative",
  "NodeJs",
  "Express",
  "MongoDB",
  "MySQL",
];

const websiteTypeOptions = [
  "E-commerce / Online Store",
  "Marketplace",
  "Business / Corporate Website",
  "News & Magazine",
  "Blog",
  "Video Streaming",
  "Portfolio Site",
  "Social Networks",
  "Community Forums",
  "E-learning Platform",
  "Health & Fitness",
  "Doctor Appointment / Medical Service Sites",
  "Travel & Booking",
  "Food Delivery Platforms",
  "Job Boards & Career Sites",
  "Banking & Financial Institutions",
  "Investment Platforms",
  "Nonprofit / Charity Websites",
  "App Landing Pages",
  "SaaS Product Websites",
];

export default function ClientProjectForm() {
  const { t } = useTranslation("common");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const projectImageInputRef = useRef(null);
  const desktopImageInputRef = useRef(null);
  const mobileImageInputRef = useRef(null);
  const [projectImagePreview, setProjectImagePreview] = useState("");
  const [desktopImagePreview, setDesktopImagePreview] = useState("");
  const [mobileImagePreview, setMobileImagePreview] = useState("");
  const [projectImageFile, setProjectImageFile] = useState(null);
  const [desktopImageFile, setDesktopImageFile] = useState(null);
  const [mobileImageFile, setMobileImageFile] = useState(null);

  const [modal, setModal] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });

  const [formData, setFormData] = useState({
    type: "web",
    title: "",
    description: "",
    techStack: [],
    projectImage: "",
    desktopModalImage: "",
    mobileModalImage: "",
    link: "",
    websiteUrl: "",
    websiteType: "",
  });

  useEffect(() => {
    return () => {
      if (projectImagePreview) URL.revokeObjectURL(projectImagePreview);
      if (desktopImagePreview) URL.revokeObjectURL(desktopImagePreview);
      if (mobileImagePreview) URL.revokeObjectURL(mobileImagePreview);
    };
  }, [projectImagePreview, desktopImagePreview, mobileImagePreview]);

  const openErrorModal = (message) => {
    setModal({
      open: true,
      type: "error",
      title: t("forms.clientProject.modals.uploadFailed"),
      message,
    });
  };

  const validateImageFile = (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      openErrorModal("Please select a valid image file (JPEG, PNG, WebP, or GIF)");
      return false;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      openErrorModal("File size must be less than 10MB");
      return false;
    }
    return true;
  };

  const setPreviewFromFile = (file, getPrevUrl, setPrevUrl) => {
    const prev = getPrevUrl();
    if (prev) URL.revokeObjectURL(prev);
    const url = URL.createObjectURL(file);
    setPrevUrl(url);
  };

  const resetInput = (ref) => {
    if (ref.current) ref.current.value = "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleTechStackChange = (selected) => {
    setFormData((p) => ({ ...p, techStack: selected }));
  };

  const handleProjectImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !validateImageFile(file)) return;
    setProjectImageFile(file);
    setPreviewFromFile(file, () => projectImagePreview, setProjectImagePreview);
    resetInput(projectImageInputRef);
  };

  const handleDesktopImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !validateImageFile(file)) return;
    setDesktopImageFile(file);
    setPreviewFromFile(file, () => desktopImagePreview, setDesktopImagePreview);
    resetInput(desktopImageInputRef);
  };

  const handleMobileImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !validateImageFile(file)) return;
    setMobileImageFile(file);
    setPreviewFromFile(file, () => mobileImagePreview, setMobileImagePreview);
    resetInput(mobileImageInputRef);
  };

  const resetForm = () => {
    setFormData({
      type: "web",
      title: "",
      description: "",
      techStack: [],
      projectImage: "",
      desktopModalImage: "",
      mobileModalImage: "",
      link: "",
      websiteUrl: "",
      websiteType: "",
    });
    if (projectImagePreview) URL.revokeObjectURL(projectImagePreview);
    if (desktopImagePreview) URL.revokeObjectURL(desktopImagePreview);
    if (mobileImagePreview) URL.revokeObjectURL(mobileImagePreview);
    setProjectImagePreview("");
    setDesktopImagePreview("");
    setMobileImagePreview("");
    setProjectImageFile(null);
    setDesktopImageFile(null);
    setMobileImageFile(null);
    resetInput(projectImageInputRef);
    resetInput(desktopImageInputRef);
    resetInput(mobileImageInputRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!projectImageFile && (!formData.projectImage || !formData.projectImage.trim())) {
      setModal({
        open: true,
        type: "error",
        title: t("forms.clientProject.modals.creationFailed"),
        message: "Please select a main project image",
      });
      return;
    }

    if (!desktopImageFile && (!formData.desktopModalImage || !formData.desktopModalImage.trim())) {
      setModal({
        open: true,
        type: "error",
        title: t("forms.clientProject.modals.creationFailed"),
        message: "Please select a desktop modal image",
      });
      return;
    }

    if (!mobileImageFile && (!formData.mobileModalImage || !formData.mobileModalImage.trim())) {
      setModal({
        open: true,
        type: "error",
        title: t("forms.clientProject.modals.creationFailed"),
        message: "Please select a mobile modal image",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let projectImageUrl = formData.projectImage;
      let desktopModalImageUrl = formData.desktopModalImage;
      let mobileModalImageUrl = formData.mobileModalImage;

      const websiteName = formData.websiteUrl.trim() || formData.title.trim() || "project";

      if (projectImageFile) {
        const result = await uploadProjectImage(projectImageFile, websiteName, "main");
        if (result.success && result.data?.imageUrl) {
          projectImageUrl = result.data.imageUrl;
        } else {
          throw new Error(result.message || "Failed to upload project image");
        }
      }

      if (desktopImageFile) {
        const result = await uploadProjectImage(desktopImageFile, websiteName, "desktop");
        if (result.success && result.data?.imageUrl) {
          desktopModalImageUrl = result.data.imageUrl;
        } else {
          throw new Error(result.message || "Failed to upload desktop image");
        }
      }

      if (mobileImageFile) {
        const result = await uploadProjectImage(mobileImageFile, websiteName, "mobile");
        if (result.success && result.data?.imageUrl) {
          mobileModalImageUrl = result.data.imageUrl;
        } else {
          throw new Error(result.message || "Failed to upload mobile image");
        }
      }

      const projectData = {
        type: formData.type,
        title: formData.title.trim(),
        description: formData.description.trim(),
        techStack: Array.isArray(formData.techStack)
          ? formData.techStack.join(", ")
          : formData.techStack,
        projectImage: projectImageUrl.trim(),
        desktopModalImage: desktopModalImageUrl.trim(),
        mobileModalImage: mobileModalImageUrl.trim(),
        link: formData.link.trim(),
        websiteUrl: formData.websiteUrl.trim(),
        websiteType: formData.websiteType.trim(),
      };

      const result = await createProject(projectData);

      if (result.success) {
        setModal({
          open: true,
          type: "success",
          title: t("forms.clientProject.modals.projectCreated"),
          message: t("forms.clientProject.modals.successMessage"),
        });
        resetForm();
      } else {
        throw new Error(result.error || "Failed to create project");
      }
    } catch (err) {
      setModal({
        open: true,
        type: "error",
        title: t("forms.clientProject.modals.creationFailed"),
        message: err?.message || t("forms.clientProject.modals.errorMessage"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.projectForm}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="type" className={styles.label}>
              {t("forms.clientProject.labels.projectType")} *
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className={styles.select}
              required
              disabled={isSubmitting}
            >
              <option value="web">
                {t("forms.clientProject.options.webApplication")}
              </option>
              <option value="mobile">
                {t("forms.clientProject.options.mobileApplication")}
              </option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="websiteType" className={styles.label}>
              {t("forms.clientProject.labels.websiteType")} *
            </label>
            <select
              id="websiteType"
              name="websiteType"
              value={formData.websiteType}
              onChange={handleInputChange}
              className={styles.select}
              required
              disabled={isSubmitting}
            >
              <option value="">
                {t("forms.clientProject.placeholders.selectWebsiteType")}
              </option>
              {websiteTypeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="title" className={styles.label}>
            {t("forms.clientProject.labels.projectTitle")} *
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className={styles.input}
            placeholder={t(
              "forms.clientProject.placeholders.enterProjectTitle"
            )}
            required
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description" className={styles.label}>
            {t("forms.clientProject.labels.projectDescription")} *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className={styles.textarea}
            placeholder={t("forms.clientProject.placeholders.describeProject")}
            rows="4"
            required
            disabled={isSubmitting}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            {t("forms.clientProject.labels.technologyStack")}
          </label>
          <MultiSelectDropdown
            options={technologyOptions}
            selectedValues={formData.techStack}
            onChange={handleTechStackChange}
            placeholder={t(
              "forms.clientProject.placeholders.selectTechnologies"
            )}
            className={styles.techStackDropdown}
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="link" className={styles.label}>
              {t("forms.clientProject.labels.projectLink")} *
            </label>
            <input
              type="url"
              id="link"
              name="link"
              value={formData.link}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t(
                "forms.clientProject.placeholders.projectUrlExample"
              )}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="websiteUrl" className={styles.label}>
              {t("forms.clientProject.labels.websiteName")}
            </label>
            <input
              type="text"
              id="websiteUrl"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleInputChange}
              className={styles.input}
              placeholder={t(
                "forms.clientProject.placeholders.websiteNameExample"
              )}
              required
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className={styles.imageSection}>
          <h3 className={styles.sectionSubtitle}>
            {t("forms.clientProject.labels.projectImages")}
          </h3>

          <div className={styles.imageUploadRow}>
            <div className={styles.formGroup}>
              <label htmlFor="projectImage" className={styles.label}>
                {t("forms.clientProject.labels.mainProjectImage")} *
              </label>
              <div className={styles.fileUploadContainer}>
                <label 
                  className={`${styles.fileInputLabel} ${isSubmitting ? styles.disabled : ''}`}
                >
                  <span className={styles.fileInputText}>
                    {(projectImageFile || formData.projectImage) ? "Change Image" : "Choose Image File"}
                  </span>
                  <input
                    type="file"
                    id="projectImage"
                    name="projectImage"
                    ref={projectImageInputRef}
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={handleProjectImageUpload}
                    className={styles.fileInput}
                    disabled={isSubmitting}
                  />
                </label>
                {(projectImagePreview || formData.projectImage) && (
                  <div className={styles.imagePreviewWrapper}>
                    <img
                      src={projectImagePreview || formData.projectImage}
                      alt="Project preview"
                      className={styles.imagePreview}
                    />
                    {(projectImageFile || formData.projectImage) && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((p) => ({ ...p, projectImage: "" }));
                          if (projectImagePreview) URL.revokeObjectURL(projectImagePreview);
                          setProjectImagePreview("");
                          setProjectImageFile(null);
                          resetInput(projectImageInputRef);
                        }}
                        className={styles.removeImageButton}
                        disabled={isSubmitting}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
                {!projectImagePreview && !projectImageFile && (
                  <p className={styles.helperText}>
                    JPEG, PNG, WebP, GIF (max 10MB)
                  </p>
                )}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="desktopModalImage" className={styles.label}>
                {t("forms.clientProject.labels.desktopModalImage")} *
              </label>
              <div className={styles.fileUploadContainer}>
                <label 
                  className={`${styles.fileInputLabel} ${isSubmitting ? styles.disabled : ''}`}
                >
                  <span className={styles.fileInputText}>
                    {(desktopImageFile || formData.desktopModalImage) ? "Change Image" : "Choose Image File"}
                  </span>
                  <input
                    type="file"
                    id="desktopModalImage"
                    name="desktopModalImage"
                    ref={desktopImageInputRef}
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={handleDesktopImageUpload}
                    className={styles.fileInput}
                    disabled={isSubmitting}
                  />
                </label>
                {(desktopImagePreview || formData.desktopModalImage) && (
                  <div className={styles.imagePreviewWrapper}>
                    <img
                      src={desktopImagePreview || formData.desktopModalImage}
                      alt="Desktop preview"
                      className={styles.imagePreview}
                    />
                    {(desktopImageFile || formData.desktopModalImage) && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((p) => ({ ...p, desktopModalImage: "" }));
                          if (desktopImagePreview) URL.revokeObjectURL(desktopImagePreview);
                          setDesktopImagePreview("");
                          setDesktopImageFile(null);
                          resetInput(desktopImageInputRef);
                        }}
                        className={styles.removeImageButton}
                        disabled={isSubmitting}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
                {!desktopImagePreview && !desktopImageFile && (
                  <p className={styles.helperText}>
                    JPEG, PNG, WebP, GIF (max 10MB)
                  </p>
                )}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="mobileModalImage" className={styles.label}>
                {t("forms.clientProject.labels.mobileModalImage")} *
              </label>
              <div className={styles.fileUploadContainer}>
                <label 
                  className={`${styles.fileInputLabel} ${isSubmitting ? styles.disabled : ''}`}
                >
                  <span className={styles.fileInputText}>
                    {(mobileImageFile || formData.mobileModalImage) ? "Change Image" : "Choose Image File"}
                  </span>
                  <input
                    type="file"
                    id="mobileModalImage"
                    name="mobileModalImage"
                    ref={mobileImageInputRef}
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={handleMobileImageUpload}
                    className={styles.fileInput}
                    disabled={isSubmitting}
                  />
                </label>
                {(mobileImagePreview || formData.mobileModalImage) && (
                  <div className={styles.imagePreviewWrapper}>
                    <img
                      src={mobileImagePreview || formData.mobileModalImage}
                      alt="Mobile preview"
                      className={styles.imagePreview}
                    />
                    {(mobileImageFile || formData.mobileModalImage) && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((p) => ({ ...p, mobileModalImage: "" }));
                          if (mobileImagePreview) URL.revokeObjectURL(mobileImagePreview);
                          setMobileImagePreview("");
                          setMobileImageFile(null);
                          resetInput(mobileImageInputRef);
                        }}
                        className={styles.removeImageButton}
                        disabled={isSubmitting}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
                {!mobileImagePreview && !mobileImageFile && (
                  <p className={styles.helperText}>
                    JPEG, PNG, WebP, GIF (max 10MB)
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="light"
            onClick={resetForm}
            disabled={isSubmitting}
          >
            {t("forms.clientProject.buttons.resetForm")}
          </Button>
          <Button type="submit" variant="default" disabled={isSubmitting}>
            {isSubmitting
              ? t("forms.clientProject.buttons.creating")
              : t("forms.clientProject.buttons.createProject")}
          </Button>
        </div>
      </form>

      <FeedbackModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={t("forms.clientProject.modals.close")}
      />
    </>
  );
}
